import { Directive, forwardRef, HostListener, Input, OnChanges } from '@angular/core';
import { BehaviorSubject, merge } from 'rxjs';
import { map } from 'rxjs/operators';
import { FeatureFlag, FeatureHubFlag } from '../services/feature-flag';
import { FeatureFlagService } from '../services/feature-flag.service';
import { Authorizer, AUTHORIZERS } from './authorizer';

@Directive({
  selector: '[authorizeFeatureFlag]',
  providers: [
    {
      provide: AUTHORIZERS,
      useExisting: forwardRef(() => AuthorizeFeatureFlagDirective),
      multi: true,
    },
  ],
  standalone: false,
})
export class AuthorizeFeatureFlagDirective implements Authorizer, OnChanges {
  // keyof typeof gives us nice auto-complete in templates
  @Input('authorizeFeatureFlag') featureFlag?:
    | keyof typeof FeatureFlag
    | keyof typeof FeatureHubFlag;

  readonly order = 0;

  private input$ = new BehaviorSubject<null>(null);

  readonly isAuthorizedChanges = merge(this.input$, this.featureFlagService.changes).pipe(
    map(
      () =>
        !this.featureFlag || this.featureFlagService.isEnabled(this.flagValue(this.featureFlag)),
    ),
  );

  constructor(private featureFlagService: FeatureFlagService) {}

  ngOnChanges() {
    this.input$.next(null);
  }

  /**
   * When opening the "enable feature" link in a new tab this ensures
   * the feature is rechecked on returning to the old tab
   */
  @HostListener('window:focus')
  onWindowFocus() {
    this.input$.next(null);
  }

  private flagValue(name: keyof typeof FeatureFlag | keyof typeof FeatureHubFlag) {
    return name in FeatureHubFlag
      ? FeatureHubFlag[name as keyof typeof FeatureHubFlag]
      : FeatureFlag[name as keyof typeof FeatureFlag];
  }
}
